import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { Me, PaidTier, SiteConfig } from './types/problem';
import { fetchConfig, fetchMe, logout as apiLogout } from './hooks/useApi';

interface AuthState {
  me: Me | null;
  config: SiteConfig | null;
  ready: boolean;
  refresh: () => Promise<Me | null>;
  setMe: (me: Me) => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

const FALLBACK_CONFIG: SiteConfig = {
  googleEnabled: false,
  paymentsEnabled: false,
  freeMonthlyGrades: 30,
  plans: [
    { key: 'plus_monthly', tier: 'plus', label: 'CodeReviser Plus (Monthly)', amount: 5000, currency: 'INR', period: 'monthly', monthlyGrades: 300 },
    { key: 'pro_monthly', tier: 'pro', label: 'CodeReviser Pro (Monthly)', amount: 10000, currency: 'INR', period: 'monthly', monthlyGrades: 0 },
  ],
  extensionVersion: '',
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [config, setConfig] = useState<SiteConfig | null>(null);
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const m = await fetchMe();
      setMe(m);
      return m;
    } catch {
      setMe({ authenticated: false });
      return null;
    }
  }, []);

  useEffect(() => {
    Promise.all([
      refresh(),
      fetchConfig().then(setConfig).catch(() => setConfig(FALLBACK_CONFIG)),
    ]).finally(() => setReady(true));
  }, [refresh]);

  const logout = useCallback(async () => {
    try { await apiLogout(); } catch {}
    setMe({ authenticated: false });
  }, []);

  return (
    <AuthContext.Provider value={{ me, config: config ?? FALLBACK_CONFIG, ready, refresh, setMe, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}

export function formatPrice(paise: number) {
  return `₹${(paise / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

export const PLAN_NAMES: Record<'free' | PaidTier, string> = { free: 'Free', plus: 'Plus', pro: 'Pro' };
